import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBasket,
  Pill,
  CalendarClock,
  Check,
  X,
  Play,
  RotateCcw,
} from "lucide-react";
import { recordFunctional } from "@/lib/neuroStore";

// -----------------------------------------------------------------------
// Real-Life Functional Training (RLT)
// Non-diagnostic exercises that mirror everyday tasks: shopping list recall,
// medication scheduling, appointment planning. Rule-based only — no LLM.
// -----------------------------------------------------------------------

const shuffle = (arr) => {
  const c = [...arr];
  for (let i = c.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [c[i], c[j]] = [c[j], c[i]];
  }
  return c;
};

// -----------------------------------------------------------------------
// Frame
// -----------------------------------------------------------------------
const Frame = ({ Icon, title, level, hint, children, testId }) => (
  <div
    data-testid={testId}
    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Level {level}
      </div>
    </div>
    {hint && (
      <p className="text-sm text-zinc-300 mb-4 leading-relaxed">{hint}</p>
    )}
    {children}
  </div>
);

const Result = ({ score, total, onRestart }) => {
  const pct = total ? Math.round((score / total) * 100) : 0;
  return (
    <div className="mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5 flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Session complete
        </div>
        <div className="mt-1 font-display text-2xl font-semibold tabular-nums">
          {score}/{total} · {pct}%
        </div>
        <div className="text-xs text-zinc-400 mt-1">
          Task-performance only. Not a medical outcome.
        </div>
      </div>
      <button
        data-testid="functional-restart"
        onClick={onRestart}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        Try again
      </button>
    </div>
  );
};

// -----------------------------------------------------------------------
// 1) Shopping List — memory + attention
// -----------------------------------------------------------------------
const SHOP_POOL = [
  "Rice", "Dal", "Onion", "Tomato", "Milk", "Bread", "Curd", "Ginger",
  "Garlic", "Banana", "Coriander", "Turmeric", "Salt", "Sugar", "Tea",
  "Oats", "Paneer", "Lemon", "Chilli", "Coconut",
];

function ShoppingList({ level = 1, onFinish }) {
  const count = 4 + Math.min(4, level); // 5..8
  const [items] = useState(() => shuffle(SHOP_POOL).slice(0, count));
  const [phase, setPhase] = useState("study"); // study -> recall -> done
  const [picked, setPicked] = useState(new Set());
  const [pool] = useState(() =>
    shuffle([...items, ...shuffle(SHOP_POOL.filter((x) => !items.includes(x))).slice(0, count)])
  );

  const submit = () => {
    const correct = items.filter((i) => picked.has(i)).length;
    const wrong = [...picked].filter((i) => !items.includes(i)).length;
    const score = Math.max(0, correct - wrong);
    recordFunctional("shopping", score, items.length);
    setPhase("done");
    onFinish?.({ score, total: items.length });
  };

  const reset = () => {
    setPicked(new Set());
    setPhase("study");
  };

  return (
    <Frame
      Icon={ShoppingBasket}
      title="Shopping list simulator"
      level={level}
      hint={
        phase === "study"
          ? `Memorise the list. You will have ${5 + level * 2} seconds.`
          : phase === "recall"
          ? "Tap every item that was on your list."
          : ""
      }
      testId="functional-shopping"
    >
      {phase === "study" && (
        <StudyBlock
          items={items}
          seconds={5 + level * 2}
          onDone={() => setPhase("recall")}
        />
      )}
      {phase === "recall" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {pool.map((it, i) => {
              const on = picked.has(it);
              return (
                <button
                  key={i}
                  data-testid={`shop-item-${i}`}
                  onClick={() => {
                    const next = new Set(picked);
                    if (on) next.delete(it);
                    else next.add(it);
                    setPicked(next);
                  }}
                  className={`text-sm rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    on
                      ? "border-cyan-400 bg-cyan-400/15 text-white"
                      : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-200"
                  }`}
                >
                  {on ? "✓ " : ""}{it}
                </button>
              );
            })}
          </div>
          <button
            data-testid="shop-submit"
            onClick={submit}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
          >
            Submit list
          </button>
        </>
      )}
      {phase === "done" && (
        <Result
          score={Math.max(
            0,
            items.filter((i) => picked.has(i)).length -
              [...picked].filter((i) => !items.includes(i)).length
          )}
          total={items.length}
          onRestart={reset}
        />
      )}
    </Frame>
  );
}

function StudyBlock({ items, seconds, onDone }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const id = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) {
          clearInterval(id);
          onDone();
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <div className="text-right text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-2">
        {left}s remaining
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="text-sm rounded-xl border border-violet-400/30 bg-violet-400/10 px-3 py-2.5 text-white"
          >
            {it}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// 2) Medication Routine — executive function
// -----------------------------------------------------------------------
const MED_SETS = [
  [
    { name: "Vitamin D", when: "Morning · with food" },
    { name: "Iron", when: "Afternoon · with water" },
    { name: "Melatonin", when: "Night · 30 min before sleep" },
  ],
  [
    { name: "Multivitamin", name_: "vit", when: "Morning · with food" },
    { name: "Antihistamine", when: "Morning · empty stomach" },
    { name: "Calcium", when: "Afternoon · with food" },
    { name: "Vitamin B12", when: "Morning · with food" },
  ],
];
const SLOTS = ["Morning", "Afternoon", "Night"];

function MedicationRoutine({ level = 1, onFinish }) {
  const set = MED_SETS[Math.min(level - 1, MED_SETS.length - 1)];
  const [assign, setAssign] = useState({}); // med -> slot
  const [phase, setPhase] = useState("play");

  const drop = (med, slot) => {
    setAssign((prev) => ({ ...prev, [med]: slot }));
  };

  const submit = () => {
    let correct = 0;
    set.forEach((m) => {
      const wantSlot = m.when.split(" ·")[0];
      if (assign[m.name] === wantSlot) correct += 1;
    });
    recordFunctional("medication", correct, set.length);
    setPhase("done");
    onFinish?.({ score: correct, total: set.length });
  };

  const reset = () => {
    setAssign({});
    setPhase("play");
  };

  return (
    <Frame
      Icon={Pill}
      title="Medication routine planner"
      level={level}
      hint="Tap a medicine, then tap the correct time-slot. Refer to the timing hint under each name."
      testId="functional-medication"
    >
      {phase === "play" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {set.map((m) => (
              <div
                key={m.name}
                data-testid={`med-${m.name.replace(/\s+/g, "-")}`}
                className={`rounded-xl border px-3 py-3 ${
                  assign[m.name]
                    ? "border-emerald-400/50 bg-emerald-400/10"
                    : "border-white/10 bg-white/5"
                }`}
              >
                <div className="text-sm font-medium text-white">{m.name}</div>
                <div className="text-xs text-zinc-400">{m.when}</div>
                <div className="mt-2 flex gap-1">
                  {SLOTS.map((s) => (
                    <button
                      key={s}
                      data-testid={`med-${m.name.replace(/\s+/g, "-")}-slot-${s}`}
                      onClick={() => drop(m.name, s)}
                      className={`text-[11px] rounded-full px-2.5 py-1 border ${
                        assign[m.name] === s
                          ? "border-cyan-400 bg-cyan-400/20 text-white"
                          : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="med-submit"
            onClick={submit}
            disabled={Object.keys(assign).length !== set.length}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400 disabled:opacity-50"
          >
            Submit routine
          </button>
        </>
      )}
      {phase === "done" && (
        <Result
          score={
            set.filter((m) => assign[m.name] === m.when.split(" ·")[0]).length
          }
          total={set.length}
          onRestart={reset}
        />
      )}
    </Frame>
  );
}

// -----------------------------------------------------------------------
// 3) Appointment Scheduling — problem solving + planning
// -----------------------------------------------------------------------
const APPT_POOL = [
  { name: "Doctor visit", duration: 90 },
  { name: "Physiotherapy", duration: 60 },
  { name: "Grocery run", duration: 45 },
  { name: "Family call", duration: 30 },
  { name: "Walk & stretch", duration: 30 },
  { name: "Lunch", duration: 45 },
];
const TIME_SLOTS = [
  { key: "09:00", label: "9:00 AM" },
  { key: "10:30", label: "10:30 AM" },
  { key: "12:00", label: "12:00 PM" },
  { key: "14:00", label: "2:00 PM" },
  { key: "15:30", label: "3:30 PM" },
  { key: "17:00", label: "5:00 PM" },
];

function AppointmentScheduling({ level = 1, onFinish }) {
  const tasks = APPT_POOL.slice(0, Math.min(6, 3 + level)); // 4..6
  const [pick, setPick] = useState({}); // taskName -> slot key
  const [phase, setPhase] = useState("play");

  const usedSlots = new Set(Object.values(pick));

  const assign = (task, slot) => {
    setPick((prev) => {
      const next = { ...prev };
      if (next[task] === slot) delete next[task];
      else next[task] = slot;
      return next;
    });
  };

  const submit = () => {
    // Correct if: doctor visit ≤ 12:00 (morning), physio between 09:00-14:00,
    // family call after 15:30, walk between 09:00-12:00, lunch at 12:00 or 14:00.
    const rules = {
      "Doctor visit": (s) => ["09:00", "10:30"].includes(s),
      Physiotherapy: (s) => ["09:00", "10:30", "12:00", "14:00"].includes(s),
      "Grocery run": (s) => ["12:00", "14:00", "15:30"].includes(s),
      "Family call": (s) => ["15:30", "17:00"].includes(s),
      "Walk & stretch": (s) => ["09:00", "10:30", "12:00"].includes(s),
      Lunch: (s) => ["12:00", "14:00"].includes(s),
    };
    let correct = 0;
    tasks.forEach((t) => {
      if (pick[t.name] && rules[t.name]?.(pick[t.name])) correct += 1;
    });
    recordFunctional("scheduling", correct, tasks.length);
    setPhase("done");
    onFinish?.({ score: correct, total: tasks.length });
  };

  const reset = () => {
    setPick({});
    setPhase("play");
  };

  return (
    <Frame
      Icon={CalendarClock}
      title="Day-scheduling planner"
      level={level}
      hint="Match each task to a sensible time-slot. Only one task per slot."
      testId="functional-scheduling"
    >
      {phase === "play" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tasks.map((t) => (
              <div
                key={t.name}
                className="rounded-xl border border-white/10 bg-white/5 p-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="text-xs text-zinc-400">{t.duration} min</div>
                  </div>
                  {pick[t.name] && (
                    <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold">
                      {TIME_SLOTS.find((s) => s.key === pick[t.name])?.label}
                    </div>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {TIME_SLOTS.map((s) => {
                    const taken =
                      usedSlots.has(s.key) && pick[t.name] !== s.key;
                    return (
                      <button
                        key={s.key}
                        data-testid={`sched-${t.name.replace(/\s+/g, "-")}-${s.key}`}
                        disabled={taken}
                        onClick={() => assign(t.name, s.key)}
                        className={`text-[11px] rounded-full px-2 py-1 border ${
                          pick[t.name] === s.key
                            ? "border-cyan-400 bg-cyan-400/20 text-white"
                            : taken
                            ? "border-white/5 bg-white/2 text-zinc-600 line-through"
                            : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="sched-submit"
            onClick={submit}
            disabled={Object.keys(pick).length !== tasks.length}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400 disabled:opacity-50"
          >
            Submit schedule
          </button>
        </>
      )}
      {phase === "done" && (
        <Result
          score={(() => {
            const rules = {
              "Doctor visit": (s) => ["09:00", "10:30"].includes(s),
              Physiotherapy: (s) =>
                ["09:00", "10:30", "12:00", "14:00"].includes(s),
              "Grocery run": (s) => ["12:00", "14:00", "15:30"].includes(s),
              "Family call": (s) => ["15:30", "17:00"].includes(s),
              "Walk & stretch": (s) => ["09:00", "10:30", "12:00"].includes(s),
              Lunch: (s) => ["12:00", "14:00"].includes(s),
            };
            return tasks.filter(
              (t) => pick[t.name] && rules[t.name]?.(pick[t.name])
            ).length;
          })()}
          total={tasks.length}
          onRestart={reset}
        />
      )}
    </Frame>
  );
}

// -----------------------------------------------------------------------
// Registry + Router
// -----------------------------------------------------------------------
export const FUNCTIONAL_EXERCISES = [
  {
    key: "shopping",
    label: "Shopping list",
    Icon: ShoppingBasket,
    accent: "#22d3ee",
    Component: ShoppingList,
    blurb: "Memorise a grocery list, then recall the items.",
  },
  {
    key: "medication",
    label: "Medication routine",
    Icon: Pill,
    accent: "#a78bfa",
    Component: MedicationRoutine,
    blurb: "Assign each medication to the correct time-of-day.",
  },
  {
    key: "scheduling",
    label: "Day scheduling",
    Icon: CalendarClock,
    accent: "#34d399",
    Component: AppointmentScheduling,
    blurb: "Plan a realistic day — one task per time-slot.",
  },
];

export default function FunctionalExercises({ level = 1 }) {
  const [active, setActive] = useState(null);
  if (active) {
    const item = FUNCTIONAL_EXERCISES.find((x) => x.key === active);
    const Cmp = item.Component;
    return (
      <div>
        <button
          data-testid="functional-back"
          onClick={() => setActive(null)}
          className="mb-3 inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
        >
          ← Back to Real-Life Training
        </button>
        <Cmp level={level} onFinish={() => setActive(null)} />
      </div>
    );
  }
  return (
    <div>
      <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 mb-5">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Real-Life Functional Training
        </div>
        <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
          Everyday-life exercises mirror the tasks that matter after cognitive
          setbacks — remembering a grocery list, taking medicines on time,
          planning a day. Task-performance only, non-diagnostic.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {FUNCTIONAL_EXERCISES.map((f) => (
          <motion.button
            key={f.key}
            data-testid={`functional-card-${f.key}`}
            whileHover={{ y: -3 }}
            onClick={() => setActive(f.key)}
            className="text-left rounded-3xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.05]"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center border"
              style={{
                borderColor: `${f.accent}80`,
                background: `${f.accent}20`,
              }}
            >
              <f.Icon className="w-5 h-5" style={{ color: f.accent }} />
            </div>
            <div className="mt-4 font-display text-lg font-semibold text-white">
              {f.label}
            </div>
            <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">
              {f.blurb}
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-300 font-semibold">
              <Play className="w-3 h-3" /> Start
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
